import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { supabase } from "../../../lib/supabaseClient";

const HERO_BG =
  "https://public.readdy.ai/ai/img_res/34e7581137c70c9664a1c95f88005d93.jpg";

const CITY_OPTIONS = ["kyoto", "osaka", "tokyo"] as const;
const LANG_OPTIONS = ["zh", "en", "ja"] as const;

type CityKey = (typeof CITY_OPTIONS)[number];
type LangKey = (typeof LANG_OPTIONS)[number];

interface FormState {
  name: string;
  email: string;
  line_or_contact: string;
  service_cities: CityKey[];
  languages: LangKey[];
  portfolio_url: string;
  profile_image_url: string;
  short_bio: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  service_cities?: string;
  languages?: string;
  portfolio_url?: string;
  short_bio?: string;
  submit?: string;
}

export default function PhotographerApplyPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    line_or_contact: "",
    service_cities: [],
    languages: [],
    portfolio_url: "",
    profile_image_url: "",
    short_bio: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toggleCity = (city: CityKey) => {
    setForm((prev) => ({
      ...prev,
      service_cities: prev.service_cities.includes(city)
        ? prev.service_cities.filter((c) => c !== city)
        : [...prev.service_cities, city],
    }));
    setErrors((prev) => ({ ...prev, service_cities: undefined }));
  };

  const toggleLang = (lang: LangKey) => {
    setForm((prev) => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter((l) => l !== lang)
        : [...prev.languages, lang],
    }));
    setErrors((prev) => ({ ...prev, languages: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = t("tabifoto.apply.error_required");
    if (!form.email.trim()) {
      newErrors.email = t("tabifoto.apply.error_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t("tabifoto.apply.error_email");
    }
    if (form.service_cities.length === 0)
      newErrors.service_cities = t("tabifoto.apply.error_city");
    if (form.languages.length === 0)
      newErrors.languages = t("tabifoto.apply.error_language");
    if (!form.portfolio_url.trim())
      newErrors.portfolio_url = t("tabifoto.apply.error_required");
    if (!form.short_bio.trim())
      newErrors.short_bio = t("tabifoto.apply.error_required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("photographer_applications").insert({
        name: form.name.trim(),
        email: form.email.trim(),
        line_or_contact: form.line_or_contact.trim() || null,
        service_cities: form.service_cities,
        languages: form.languages,
        portfolio_url: form.portfolio_url.trim(),
        profile_image_url: form.profile_image_url.trim() || null,
        short_bio: form.short_bio.trim(),
        status: "pending",
      });
      if (error) throw error;
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setErrors((prev) => ({
        ...prev,
        submit: t("tabifoto.apply.error_submit"),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] font-['Cormorant_Garamond',serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />
        <div className="relative w-full max-w-5xl mx-auto px-6 lg:px-10 pb-14">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">
            Closed Beta · 2026
          </p>
          <h1 className="text-4xl lg:text-5xl font-light text-white leading-tight mb-3">
            {t("tabifoto.apply.page_title")}
          </h1>
          <p className="text-sm text-white/60 tracking-wider">
            {t("tabifoto.apply.page_sub")}
          </p>
        </div>
      </section>

      {/* Success state */}
      {submitted ? (
        <section className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#C8B8A6]/20">
            <i className="ri-check-line text-2xl text-[#C8B8A6]" />
          </div>
          <h2 className="text-2xl font-light text-[#2C2C2C] mb-4 tracking-wide">
            {t("tabifoto.apply.success_title")}
          </h2>
          <p className="text-sm text-[#2C2C2C]/60 leading-relaxed mb-10">
            {t("tabifoto.apply.success_body")}
          </p>
          <Link
            to="/"
            className="inline-block px-8 py-3 rounded-full border border-[#2C2C2C]/30 text-xs tracking-widest uppercase text-[#2C2C2C]/70 hover:bg-[#2C2C2C] hover:text-white hover:border-[#2C2C2C] transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Back to Home
          </Link>
        </section>
      ) : (
        <>
          {/* Philosophy + Requirements */}
          <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Philosophy */}
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#C8B8A6] mb-5">
                  {t("tabifoto.apply.philosophy_title")}
                </p>
                <p className="text-[#2C2C2C]/70 text-sm leading-relaxed mb-8">
                  {t("tabifoto.apply.philosophy_body")}
                </p>
                <ul className="space-y-4">
                  {(["philosophy_point_1", "philosophy_point_2", "philosophy_point_3"] as const).map(
                    (key) => (
                      <li key={key} className="flex items-start gap-3">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-checkbox-circle-line text-[#C8B8A6] text-base" />
                        </div>
                        <span className="text-sm text-[#2C2C2C]/70">
                          {t(`tabifoto.apply.${key}`)}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </div>

              {/* Requirements */}
              <div className="space-y-8">
                <p className="text-xs uppercase tracking-[0.25em] text-[#C8B8A6] mb-5">
                  {t("tabifoto.apply.requirements_title")}
                </p>
                {(
                  [
                    { title: "req_language_title", body: "req_language_body", icon: "ri-translate-2" },
                    { title: "req_cities_title", body: "req_cities_body", icon: "ri-map-pin-2-line" },
                    { title: "req_portfolio_title", body: "req_portfolio_body", icon: "ri-image-2-line" },
                  ] as const
                ).map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-lg text-[#C8B8A6]`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#2C2C2C] mb-1">
                        {t(`tabifoto.apply.${item.title}`)}
                      </p>
                      <p className="text-sm text-[#2C2C2C]/55 leading-relaxed">
                        {t(`tabifoto.apply.${item.body}`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div className="border-t border-[#D9D9D9]/60" />
          </div>

          {/* Form */}
          <section className="max-w-2xl mx-auto px-6 lg:px-10 py-20">
            <p className="text-xs uppercase tracking-[0.25em] text-[#C8B8A6] mb-2">
              {t("tabifoto.apply.form_title")}
            </p>
            <p className="text-sm text-[#2C2C2C]/50 mb-10">
              {t("tabifoto.apply.form_note")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-8" noValidate>
              {/* Name */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_name")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder={t("tabifoto.apply.field_name_placeholder")}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
                {errors.name && (
                  <p className="text-xs text-red-400 mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_email")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder={t("tabifoto.apply.field_email_placeholder")}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
                {errors.email && (
                  <p className="text-xs text-red-400 mt-1">{errors.email}</p>
                )}
              </div>

              {/* LINE / Contact */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_line_or_contact")}
                </label>
                <input
                  type="text"
                  value={form.line_or_contact}
                  onChange={(e) => setField("line_or_contact", e.target.value)}
                  placeholder={t("tabifoto.apply.field_line_or_contact_placeholder")}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
              </div>

              {/* Service Cities */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-3">
                  {t("tabifoto.apply.field_service_cities")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {CITY_OPTIONS.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => toggleCity(city)}
                      className={`px-5 py-2 rounded-full text-xs tracking-widest uppercase border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        form.service_cities.includes(city)
                          ? "bg-[#2C2C2C] text-[#F7F6F3] border-[#2C2C2C]"
                          : "bg-transparent text-[#2C2C2C]/60 border-[#D9D9D9] hover:border-[#C8B8A6]"
                      }`}
                    >
                      {t(`tabifoto.apply.city_${city}`)}
                    </button>
                  ))}
                </div>
                {errors.service_cities && (
                  <p className="text-xs text-red-400 mt-1">{errors.service_cities}</p>
                )}
              </div>

              {/* Languages */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-3">
                  {t("tabifoto.apply.field_languages")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {LANG_OPTIONS.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLang(lang)}
                      className={`px-5 py-2 rounded-full text-xs tracking-widest uppercase border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        form.languages.includes(lang)
                          ? "bg-[#2C2C2C] text-[#F7F6F3] border-[#2C2C2C]"
                          : "bg-transparent text-[#2C2C2C]/60 border-[#D9D9D9] hover:border-[#C8B8A6]"
                      }`}
                    >
                      {t(`tabifoto.apply.lang_${lang}`)}
                    </button>
                  ))}
                </div>
                {errors.languages && (
                  <p className="text-xs text-red-400 mt-1">{errors.languages}</p>
                )}
              </div>

              {/* Portfolio URL */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_portfolio_url")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <input
                  type="url"
                  value={form.portfolio_url}
                  onChange={(e) => setField("portfolio_url", e.target.value)}
                  placeholder={t("tabifoto.apply.field_portfolio_url_placeholder")}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
                {errors.portfolio_url && (
                  <p className="text-xs text-red-400 mt-1">{errors.portfolio_url}</p>
                )}
              </div>

              {/* Profile Image URL */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_profile_image_url")}
                </label>
                <input
                  type="url"
                  value={form.profile_image_url}
                  onChange={(e) => setField("profile_image_url", e.target.value)}
                  placeholder={t("tabifoto.apply.field_profile_image_url_placeholder")}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                />
              </div>

              {/* Short Bio */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                  {t("tabifoto.apply.field_short_bio")}
                  <span className="text-[#C8B8A6] ml-1">*</span>
                </label>
                <textarea
                  value={form.short_bio}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setField("short_bio", e.target.value);
                  }}
                  placeholder={t("tabifoto.apply.field_short_bio_placeholder")}
                  rows={5}
                  maxLength={500}
                  className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/25 focus:outline-none focus:border-[#2C2C2C] transition-colors resize-none leading-relaxed"
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.short_bio ? (
                    <p className="text-xs text-red-400">{errors.short_bio}</p>
                  ) : (
                    <span />
                  )}
                  <span className="text-xs text-[#2C2C2C]/30">
                    {form.short_bio.length} / 500
                  </span>
                </div>
              </div>

              {/* Submit error */}
              {errors.submit && (
                <p className="text-xs text-red-400">{errors.submit}</p>
              )}

              {/* Submit button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 rounded-full border border-[#2C2C2C] text-xs tracking-[0.2em] uppercase text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-[#F7F6F3] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? t("tabifoto.apply.submitting")
                    : t("tabifoto.apply.submit")}
                </button>
              </div>
            </form>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
